import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, MapPin, Clock, Ticket, Loader2, CheckCircle,
  Heart, Package, Gift, DollarSign, Plus, Minus
} from 'lucide-react';
import axios from 'axios';
import { TransparencyNotice, TransparencyCheckbox } from './TransparencyNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DONATION_PRESETS = [25, 50, 70];

const RsvpModeCard = ({ icon: Icon, title, description, selected, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
      selected ? 'border-[#BA1826] bg-[#FEF2F2]' : 'border-[#E5E7EB] bg-white hover:border-[#BA1826]/30'
    }`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${selected ? 'bg-[#BA1826]' : 'bg-[#F8F9FA]'}`}>
        <Icon className={`w-5 h-5 ${selected ? 'text-white' : 'text-[#BA1826]'}`} />
      </div>
      <div>
        <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">{title}</h4>
        <p className="text-[10px] text-[#4B5563] mt-0.5">{description}</p>
      </div>
    </div>
  </button>
);

const DonationSelector = ({ amount, setAmount }) => {
  const [custom, setCustom] = useState(false);
  return (
    <div className="space-y-2" data-testid="donation-selector">
      <label className="text-xs font-medium text-[#0A0A0A]">Select donation amount</label>
      <div className="grid grid-cols-3 gap-2">
        {DONATION_PRESETS.map(v => (
          <button key={v} onClick={() => { setAmount(v); setCustom(false); }}
            className={`py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
              amount === v && !custom ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white text-[#0A0A0A] border-[#E5E7EB] hover:border-[#BA1826]/40'
            }`} data-testid={`preset-${v}`}>
            ${v}
          </button>
        ))}
      </div>
      <button onClick={() => setCustom(true)}
        className={`w-full py-2 rounded-lg text-xs font-semibold border transition-colors ${
          custom ? 'border-[#BA1826] bg-[#FEF2F2]' : 'border-[#E5E7EB] bg-white hover:border-[#BA1826]/30'
        }`}>
        Custom Amount
      </button>
      {custom && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#4B5563]">$</span>
          <input type="number" min="5" step="1" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
            className="flex-1 px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none"
            autoFocus data-testid="custom-amount-input" />
        </div>
      )}
    </div>
  );
};

const ItemSelector = ({ suggestedItems, commitments, setCommitments }) => {
  const enabledItems = (suggestedItems?.items || []).filter(i => i.enabled !== false && i.name);

  const updateQty = (itemName, delta) => {
    setCommitments(prev => {
      const existing = prev.find(c => c.item_name === itemName);
      if (existing) {
        const newQty = Math.max(0, existing.quantity + delta);
        if (newQty === 0) return prev.filter(c => c.item_name !== itemName);
        return prev.map(c => c.item_name === itemName ? { ...c, quantity: newQty } : c);
      }
      if (delta > 0) return [...prev, { item_name: itemName, quantity: 1 }];
      return prev;
    });
  };

  const getQty = (name) => commitments.find(c => c.item_name === name)?.quantity || 0;

  return (
    <div className="space-y-2" data-testid="item-selector">
      <label className="text-xs font-medium text-[#0A0A0A]">Select items you'll bring</label>
      {suggestedItems?.header_text && (
        <p className="text-[10px] text-[#4B5563] italic">{suggestedItems.header_text}</p>
      )}
      <div className="space-y-2">
        {enabledItems.map((item) => {
          const qty = getQty(item.name);
          return (
            <div key={item.name} className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
              qty > 0 ? 'border-[#BA1826] bg-[#FEF2F2]' : 'border-[#E5E7EB] bg-white'
            }`} data-testid={`item-row-${item.name.replace(/\s/g, '-').toLowerCase()}`}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full bg-[#BA1826] flex-shrink-0" />
                <span className="text-sm text-[#0A0A0A] truncate">{item.name}</span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {qty > 0 && (
                  <button onClick={() => updateQty(item.name, -1)}
                    className="w-7 h-7 rounded-full bg-[#E5E7EB] flex items-center justify-center hover:bg-[#D1D5DB]">
                    <Minus className="w-3 h-3" />
                  </button>
                )}
                {qty > 0 && <span className="text-sm font-bold text-[#BA1826] w-6 text-center">{qty}</span>}
                <button onClick={() => updateQty(item.name, 1)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                    qty > 0 ? 'bg-[#BA1826] text-white' : 'bg-[#F8F9FA] text-[#4B5563] hover:bg-[#E5E7EB]'
                  }`}>
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {commitments.length > 0 && (
        <p className="text-[10px] text-[#BA1826] font-semibold">
          {commitments.reduce((s, c) => s + c.quantity, 0)} item{commitments.reduce((s, c) => s + c.quantity, 0) !== 1 ? 's' : ''} selected
        </p>
      )}
    </div>
  );
};

const ActiveEventRSVP = ({ event }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [rsvpMode, setRsvpMode] = useState(null);
  const [donationAmount, setDonationAmount] = useState(25);
  const [itemCommitments, setItemCommitments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [transparencyAcknowledged, setTransparencyAcknowledged] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState('');

  const isPaid = event.ticket_type === 'paid' && event.ticket_price > 0;
  const isCompleted = event.status === 'completed';
  const maxCap = event.max_capacity || 0;
  const isFull = maxCap > 0 && (event.rsvp_count || 0) >= maxCap;
  const canRsvp = event.rsvp_enabled && !isCompleted && !isFull;
  const hasSuggestedItems = event.suggested_items?.enabled && (event.suggested_items?.items || []).some(i => i.enabled !== false && i.name);
  const showDonationOption = event.donation_rsvp_enabled;

  const needsPayment = rsvpMode === 'donation' || rsvpMode === 'both' || isPaid;
  const hasItems = rsvpMode === 'item' || rsvpMode === 'both';
  const canSubmit = name.trim() && email.trim() && rsvpMode &&
    (needsPayment ? transparencyAcknowledged && donationAmount >= 5 : true) &&
    (hasItems ? itemCommitments.length > 0 : true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const rsvpType = needsPayment ? 'donation' : 'free';
      const res = await axios.post(`${BACKEND_URL}/api/public/events/${event.id}/rsvp`, {
        name: name.trim(), email: email.trim(), phone: phone.trim(),
        ticket_quantity: 1,
        rsvp_type: rsvpType,
        rsvp_mode: rsvpMode,
        donation_amount: needsPayment ? donationAmount : 0,
        item_commitments: hasItems ? itemCommitments : [],
      });
      if (res.data.type === 'paid' && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setConfirmMsg(event.confirmation_message || 'RSVP confirmed! We look forward to seeing you.');
      setSuccess(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error submitting RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  const getButtonLabel = () => {
    if (submitting) return <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>;
    if (rsvpMode === 'donation') return <><Heart className="w-4 h-4" /> RSVP with ${donationAmount} Donation</>;
    if (rsvpMode === 'item') return <><Package className="w-4 h-4" /> Confirm RSVP with Items</>;
    if (rsvpMode === 'both') return <><Gift className="w-4 h-4" /> RSVP with ${donationAmount} + Items</>;
    return <><Ticket className="w-4 h-4" /> Confirm RSVP</>;
  };

  return (
    <section className="py-8 bg-white border-b border-[#E5E7EB]" data-testid="active-event-rsvp">
      <div className="px-6 max-w-xl mx-auto">
        {event.flyer_image && (
          <div className="rounded-lg overflow-hidden mb-6 shadow-sm">
            <img src={event.flyer_image} alt={event.title} className="w-full object-cover max-h-[300px]" />
          </div>
        )}

        <div className="mb-6">
          <div className="flex flex-wrap gap-2 mb-2">
            {!isCompleted && <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded">Upcoming</span>}
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${isPaid ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
              {isPaid ? `$${event.ticket_price}` : 'Free'}
            </span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-3" data-testid="active-event-title">{event.title}</h2>
          <div className="flex flex-wrap gap-4 text-sm text-[#4B5563] mb-3">
            {event.date && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#BA1826]" /> {event.date}</span>}
            {event.time && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#BA1826]" /> {event.time}</span>}
            {event.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#BA1826]" /> {event.location}</span>}
          </div>
          {event.description && <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-line">{event.description}</p>}
          {maxCap > 0 && !isFull && <p className="text-xs text-[#4B5563] mt-2">{maxCap - (event.rsvp_count || 0)} spots remaining</p>}
        </div>

        {canRsvp && !success && (
          <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-5" data-testid="inline-rsvp-form">
            <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#BA1826]" />
              {isPaid ? 'Get Your Ticket' : 'RSVP for This Event'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Contact Info */}
              <div className="space-y-3">
                <input type="text" placeholder="Full name *" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-3 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none bg-white"
                  required data-testid="inline-rsvp-name" />
                <input type="email" placeholder="Email address *" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-3 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none bg-white"
                  required data-testid="inline-rsvp-email" />
                <input type="tel" placeholder="Phone number (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-3 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none bg-white"
                  data-testid="inline-rsvp-phone" />
              </div>

              {/* RSVP Mode Selection — 3 Cards */}
              {(showDonationOption || hasSuggestedItems) && (
                <div className="space-y-2" data-testid="rsvp-mode-selector">
                  <label className="text-xs font-medium text-[#0A0A0A]">How would you like to RSVP?</label>
                  {showDonationOption && (
                    <RsvpModeCard icon={DollarSign} title="RSVP with Donation"
                      description="Support with a financial contribution"
                      selected={rsvpMode === 'donation'} onClick={() => { setRsvpMode('donation'); setTransparencyAcknowledged(false); }}
                      testId="rsvp-mode-donation" />
                  )}
                  {hasSuggestedItems && (
                    <RsvpModeCard icon={Package} title="RSVP by Bringing an Item"
                      description="Commit to bringing supplies for the event"
                      selected={rsvpMode === 'item'} onClick={() => setRsvpMode('item')}
                      testId="rsvp-mode-item" />
                  )}
                  {showDonationOption && hasSuggestedItems && (
                    <RsvpModeCard icon={Gift} title="RSVP with Both"
                      description="Donate and bring items — maximum impact"
                      selected={rsvpMode === 'both'} onClick={() => { setRsvpMode('both'); setTransparencyAcknowledged(false); }}
                      testId="rsvp-mode-both" />
                  )}
                  {!showDonationOption && !hasSuggestedItems && (
                    <RsvpModeCard icon={Ticket} title="Free RSVP"
                      description="Just confirm your attendance"
                      selected={rsvpMode === 'free'} onClick={() => setRsvpMode('free')}
                      testId="rsvp-mode-free" />
                  )}
                </div>
              )}

              {/* If no special options, auto-select free */}
              {!showDonationOption && !hasSuggestedItems && !rsvpMode && (
                <div className="hidden">{setTimeout(() => setRsvpMode('free'), 0) && ''}</div>
              )}

              {/* Donation Amount Selector */}
              {(rsvpMode === 'donation' || rsvpMode === 'both') && (
                <DonationSelector amount={donationAmount} setAmount={setDonationAmount} />
              )}

              {/* Item Selector */}
              {(rsvpMode === 'item' || rsvpMode === 'both') && hasSuggestedItems && (
                <ItemSelector
                  suggestedItems={event.suggested_items}
                  commitments={itemCommitments}
                  setCommitments={setItemCommitments}
                />
              )}

              {/* Transparency Notice */}
              {needsPayment && (
                <>
                  <TransparencyNotice />
                  <TransparencyCheckbox checked={transparencyAcknowledged} onChange={(e) => setTransparencyAcknowledged(e.target.checked)} />
                </>
              )}

              {/* Submit */}
              <button type="submit" disabled={submitting || !canSubmit}
                className="w-full btn-primary text-sm py-3 disabled:opacity-50 mt-2 flex items-center justify-center gap-2"
                data-testid="inline-rsvp-submit">
                {getButtonLabel()}
              </button>
            </form>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center" data-testid="inline-rsvp-confirmation">
            <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <h3 className="font-heading text-xl font-bold text-green-800 mb-2">You're Registered!</h3>
            <p className="text-sm text-green-700 mb-4">{confirmMsg}</p>
            {itemCommitments.length > 0 && (
              <div className="bg-white border border-green-200 rounded-lg p-3 mb-4 text-left">
                <p className="text-xs font-semibold text-green-800 mb-1">Items you're bringing:</p>
                {itemCommitments.map((c, i) => (
                  <p key={i} className="text-xs text-green-700">{c.item_name} x{c.quantity}</p>
                ))}
              </div>
            )}
            <Link to="/donate" className="inline-flex items-center gap-2 text-sm text-[#BA1826] font-semibold hover:underline">
              <Heart className="w-4 h-4" /> Want to support our mission? Donate here
            </Link>
          </div>
        )}

        {isFull && !isCompleted && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-5 text-center">
            <p className="text-sm font-semibold text-red-700">This event has reached capacity.</p>
          </div>
        )}

        <div className="text-center mt-4">
          <Link to={`/events/${event.id}`} className="text-xs text-[#4B5563] hover:text-[#BA1826]" data-testid="view-full-event-link">
            View full event details
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ActiveEventRSVP;
